import React, { useMemo } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Alignment,
  Link,
  List,
  ListProperties,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageUpload,
  Base64UploadAdapter,
  BlockQuote,
  HorizontalLine,
  Heading,
  Undo
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

const CKMailEditor = ({
  content = '',
  onChange,
  placeholder = '메일 본문 내용을 작성해 주세요...',
  minHeight = '360px'
}) => {
  const editorConfig = useMemo(() => {
    return {
      licenseKey: 'GPL',
      plugins: [
        Essentials,
        Paragraph,
        Heading,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        FontFamily,
        FontSize,
        FontColor,
        FontBackgroundColor,
        Alignment,
        Link,
        List,
        ListProperties,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        TableColumnResize,
        Image,
        ImageToolbar,
        ImageCaption,
        ImageStyle,
        ImageResize,
        ImageUpload,
        Base64UploadAdapter,
        BlockQuote,
        HorizontalLine,
        Undo
      ],
      toolbar: {
        items: [
          'fontFamily',
          'fontSize',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'alignment',
          '|',
          'bulletedList',
          'numberedList',
          '|',
          'insertTable',
          'uploadImage',
          'link',
          'blockQuote',
          'horizontalLine',
          '|',
          'undo',
          'redo'
        ],
        shouldNotGroupWhenFull: true
      },
      fontFamily: {
        options: [
          'default',
          'Pretendard, sans-serif',
          '맑은 고딕, Malgun Gothic, sans-serif',
          '나눔고딕, Nanum Gothic, sans-serif',
          '돋움, Dotum, sans-serif',
          '굴림, Gulim, sans-serif',
          '바탕, Batang, serif',
          'Arial, Helvetica, sans-serif',
          'Courier New, Courier, monospace',
          'Georgia, serif',
          'Lucida Sans Unicode, Lucida Grande, sans-serif',
          'Tahoma, Geneva, sans-serif',
          'Times New Roman, Times, serif',
          'Trebuchet MS, Helvetica, sans-serif',
          'Verdana, Geneva, sans-serif'
        ],
        supportAllValues: true
      },
      fontSize: {
        options: [
          '9px',
          '10px',
          '11px',
          '12px',
          '13px',
          '14px',
          '16px',
          '18px',
          '20px',
          '24px',
          '28px',
          '32px'
        ],
        supportAllValues: true
      },
      fontColor: {
        colors: [
          { color: '#000000', label: '검정' },
          { color: '#4b5563', label: '회색' },
          { color: '#4f46e5', label: '인디고' },
          { color: '#2563eb', label: '파랑' },
          { color: '#059669', label: '초록' },
          { color: '#e11d48', label: '빨강' },
          { color: '#d97706', label: '오렌지' },
          { color: '#7c3aed', label: '보라' }
        ],
        columns: 4
      },
      fontBackgroundColor: {
        colors: [
          { color: '#ffffff', label: '흰색' },
          { color: '#fef08a', label: '노랑 형광' },
          { color: '#bbf7d0', label: '초록 형광' },
          { color: '#bfdbfe', label: '파랑 형광' },
          { color: '#fecdd3', label: '분홍 형광' },
          { color: '#f1f5f9', label: '연회색' }
        ],
        columns: 3
      },
      table: {
        contentToolbar: [
          'tableColumn',
          'tableRow',
          'mergeTableCells',
          'tableProperties',
          'tableCellProperties'
        ]
      },
      image: {
        toolbar: [
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:side',
          '|',
          'toggleImageCaption',
          'imageTextAlternative',
          '|',
          'imageResize'
        ]
      },
      placeholder: placeholder,
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://'
      }
    };
  }, [placeholder]);

  return (
    <div className="ckeditor-mail-wrapper w-full text-slate-900">
      <style>{`
        .ckeditor-mail-wrapper .ck.ck-editor {
          width: 100% !important;
          border-radius: 0.75rem !important;
          overflow: hidden;
          border: 1px solid #cbd5e1;
          background: #ffffff;
        }
        .ckeditor-mail-wrapper .ck.ck-toolbar {
          background-color: #f8fafc !important;
          border: none !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 6px 8px !important;
        }
        .ckeditor-mail-wrapper .ck-content {
          min-height: ${minHeight} !important;
          font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', sans-serif !important;
          font-size: 14px !important;
          line-height: 1.6 !important;
          padding: 16px 20px !important;
          color: #0f172a !important;
          background-color: #ffffff !important;
          outline: none !important;
        }
        .ckeditor-mail-wrapper .ck-content:focus {
          border-color: #6366f1 !important;
          box-shadow: inset 0 0 0 1px #6366f1 !important;
        }
        .ckeditor-mail-wrapper .ck-content img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 4px;
        }
        .ckeditor-mail-wrapper .ck-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 12px 0;
        }
        .ckeditor-mail-wrapper .ck-content table td,
        .ckeditor-mail-wrapper .ck-content table th {
          border: 1px solid #cbd5e1;
          padding: 8px 12px;
        }
        .ckeditor-mail-wrapper .ck.ck-button:hover {
          background-color: #e2e8f0 !important;
        }
        .ckeditor-mail-wrapper .ck.ck-button.ck-on {
          background-color: #e0e7ff !important;
          color: #4338ca !important;
        }
      `}</style>

      <CKEditor
        editor={ClassicEditor}
        config={editorConfig}
        data={content || ''}
        onChange={(event, editor) => {
          const data = editor.getData();
          if (onChange) {
            const tempEl = document.createElement('div');
            tempEl.innerHTML = data;
            const plainText = tempEl.innerText || tempEl.textContent || '';
            onChange(data, plainText);
          }
        }}
      />
    </div>
  );
};

export default CKMailEditor;
